/**
 * Training SOP — Crew Scheduling track.
 *
 * For the two roles that edit the company-wide crew schedule:
 *   • ManpowerEditor — `manpower.edit`, the full schedule plus the Field
 *     Leaders roster and the Available MP / day figure.
 *   • FabManpowerEditor — `manpower.edit.fab`, Fabrication-division jobs only
 *     (both crew lines), enforced server-side. The grid hides the row-tool
 *     cluster from them entirely (ManpowerPage `!fabEditOnly`), so their
 *     topics are the shared editing moves plus one scoping topic of their own.
 *
 * ONE TRACK FOR BOTH, filtered by access rules — a fab editor's rendered page
 * is the shared concepts plus fab-scoped editing, with the full-editor tools
 * (`📅 Set up`, row housekeeping, Leaders CRUD, Available MP) dropped by their
 * `manpower.edit` gate. Same mechanism the rest of the guide already uses.
 *
 * ⚠️ Facts here that are easy to get wrong, verified against the code
 * (2026-08-18 sweep of ManpowerPage/AssignmentTray/CrewSetupModal/LeadersView,
 * manpower.controller, manpower.service):
 *   • `✎ Edit Schedule` exists only on the 2/4-week (daily) ranges.
 *   • Publish/Reset touch ONLY the signed-in editor's drafts (product owner, 2026-07-25).
 *   • The row `✕` (clear future crew) is LIVE, not staged — it shares the
 *     DELETE route with Sync Projects.
 *   • Weekly figures are the week's PEAK DAY, never a sum.
 *   • A fab editor's page opens on the Fabrication scope; everyone else's on
 *     Self-Perform, which leaves fab jobs off the grid entirely.
 *   • The Start ≠ Crew chip (#385) ISOLATES — shows only — where every other
 *     chip on that row hides. The GRID never badges Complete/Canceled jobs
 *     (useScheduleData drops them), but the job's Schedule & Dates card has no
 *     status check and can still show its banner — so say "on the grid".
 *     Both inputs refresh on a five-minute poll.
 *   • ⚠️ Sync Projects is ADMIN-ONLY (`isAdmin &&` in ManpowerPage). Do not send
 *     a scheduler to it — the first draft of mp-start-mismatch did, and most
 *     readers of this track cannot see the button (2026-09-23).
 */
import { type Track, OWNERSHIP } from '../types';

export const MANPOWER_TRACK: Track = {
  id: 'manpower',
  label: 'Crew Scheduling',
  audience:
    'Schedulers who plan crews across every job, and fabrication editors who stage the shop\'s own schedule. Your screen is the company-wide Manpower page.',
  suggestedFor: { anyRole: ['ManpowerEditor', 'FabManpowerEditor'] },
  /**
   * Track-level gate matching the /manpower route's flGuard: a field-leader-
   * only reader is redirected from the page itself, and every topic in here is
   * either `hideFromFieldLeaderOnly` or permission-gated — so without this the
   * picker offered them a track that rendered an empty body (Codex, #353,
   * third round; the Administrator-track lesson again).
   */
  access: { hideFromFieldLeaderOnly: true },
  sections: [
    // ── 1. Reading the board ────────────────────────────────────────────────
    {
      id: 'mp-board',
      title: 'Reading the schedule board',
      intro:
        'The planned crew schedule, forward-looking, across every job. Learn to read it before you edit it.',
      topics: [
        {
          id: 'mp-two-screens',
          title: 'Two screens are called Manpower',
          summary:
            'Top navigation ▸ Manpower is the planned crew schedule looking forward — it is where crews are assigned. The Manpower tab inside a job is a different screen with the same name: hours already worked, from payroll.',
          watchOut: [
            'Nothing you stage or publish changes the project tab\'s actual hours — those come from Paycor. What your publish feeds is each job\'s **Scheduled Crew** card and its planned-vs-actual comparison.',
          ],
          deepLink: '/manpower',
          access: { hideFromFieldLeaderOnly: true },
        },
        {
          id: 'mp-scope-lens',
          title: 'Self-Perform, All Divisions, Fabrication',
          summary:
            'The three scope buttons decide which jobs are on the grid. **Self-Perform** is every division except Fabrication — fab crews are shop labor, so counting them would overstate the crew available for field work. The page opens on Self-Perform, or on **Fabrication** if fabrication is the only part of the schedule you can edit.',
          watchOut: [
            'A fab job that seems to be missing is a scope question, not a crew one — Self-Perform leaves it **off the grid entirely** rather than showing an empty row. Check the scope buttons before chasing anyone.',
            'A link carries the scope only when you have switched away from your own default view. Copied from your usual view it carries none, and opens on the **recipient\'s** default instead — so say which scope you mean when you share one.',
          ],
          deepLink: '/manpower',
          access: { hideFromFieldLeaderOnly: true },
        },
        {
          id: 'mp-reading-grid',
          title: 'Reading the grid',
          // "in those weeks", not "no crew booked": the Needs Scheduling rule
          // reads the VISIBLE window only, so a job can sit there while crew is
          // booked months out — and since #385 the Start ≠ Crew badge beside it
          // then names that date. The old wording contradicted the badge.
          summary:
            'Jobs group into sections — **⚠ Needs Scheduling** first (jobs starting in the visible window with no crew booked in those weeks), then each division with its subtotal, with **ClosedWon** last. The pinned rows keep the running total against capacity.',
          steps: [
            'Pick a window with **Range** — **2 wk** and **4 wk** show days; **6 / 8 / 13 wk** show weeks. Move with **‹ Today ›**.',
            'Narrow the list with the search box, **Sort**, the **PM** filter, or the **Show:** section chips.',
            'Watch **Grand Total** against **Available MP / day** — it turns yellow when the company is over capacity, and the **Headroom** row at the bottom shows what is left each day.',
            // Row links shipped with #385. Middle-click is the point: working
            // down a list means keeping the grid where it is.
            'Click a job number or name to open that job — middle-click opens it in a new tab, so the grid stays where you left it.',
          ],
          watchOut: [
            'A weekly figure is the week\'s **peak day**, never a sum of the days.',
            '**Temps active**, beside **Available MP / day**, is how many temps are working now, typed in by the manpower scheduler. They are already counted in Available MP, not extra on top of it, and the figure is not tied to any crew on the grid. The date under it says when it was last entered.',
            '**Subcontractors**, beside **Temps active**, is how many subcontractor workers are on our local jobsites now, filling gaps for specific tasks, typed in by the Training and Crew Scheduling Manager. Like temps, they are already counted in Available MP, not extra on top of it. It is also one current figure with the date it was last entered, not tied to any crew on the grid.',
            'A number on a SYSTAC row with no crew booked is the **bid forecast** — crew size times duration, carried from Bid Tracker. It counts toward the **Grand Total** and **Headroom** like any other crew, so the **SYSTAC** chip is how you compare: off shows the picture without the pursuits, on shows what happens if they all land. The bid gives a duration, not a work pattern, so a forecast fills **working days only** — weekends stay clear unless someone books crew on them. Hover a cell to see where its number came from. An **S** chip on a cell is a survey visit, not crew.',
          ],
          deepLink: '/manpower',
          access: { hideFromFieldLeaderOnly: true },
        },
        {
          // #385. Written for the scheduler as ONE of two owners — the flag
          // exists because the PM and the scheduler each held half the answer
          // and neither could see the other's. Hence the split-owner chip and a
          // step that ends in a conversation rather than a fix.
          id: 'mp-start-mismatch',
          title: 'When the crew and the planned start disagree',
          // ⚠️ The BADGE is the signal, not the color. Row tint is first-match-
          // wins in ManpowerPage — maintenance blue, then Needs Scheduling amber,
          // then Start ≠ Crew rose — so a flagged job that is also a maintenance
          // parent, or also needs scheduling this window, keeps its own color.
          // The first draft said "a rose row", which taught readers to scan for
          // a tint two kinds of flagged row never get (Codex, #545).
          summary:
            'A **⚠ Start ≠ Crew** badge on a row means the first day crew is booked on that job is not the start date the PM has on the project. Go by the badge, not the color: most flagged rows turn rose, but one already amber for Needs Scheduling, or blue for maintenance, keeps its color.',
          owner: OWNERSHIP.startAlignment,
          steps: [
            'Hover **⚠ Start ≠ Crew** to see both dates — the planned start from the project, and the first day crew is booked.',
            // N follows the scope (#540 made the count match what pressing the
            // chip shows), so on Self-Perform it silently leaves out every
            // fabrication job — worth one clause, because the count reads as
            // "how many disagree" and on the default view it is not all of them.
            'Click **⚠ Start ≠ Crew (N)** on the filter row to show only those jobs — N counts only what your current scope and filters show. Click it again to clear it; your other filters stay on.',
            // ⚠️ "and Publish". The flag reads PUBLISHED crew, the same as Needs
            // Scheduling, so moving crew only stages a draft and the badge — and
            // the PM's banner — stay put. Without this a scheduler following the
            // step stops with the mismatch still showing (Codex, #545).
            //
            // "Or remove bookings that should not be there": a wrong booking is
            // fixed by editing it out, and once no published crew is left
            // startMismatch returns null — so "clears once the two match" was
            // incomplete. ⚠️ Deliberately NOT the row's ✕ clear, which a review
            // suggested: clearFutureEntries deletes EVERY entry for the job from
            // today on, live, with no publish step to catch it — one bad booking
            // is not a reason to wipe the job's good crew. And fab editors do not
            // have ✕ at all (mp-row-tools is manpower.edit only) (Codex, #545).
            'The PM sees the same flag on the job\'s **Schedule & Dates** card. Agree with them which date is right, then either fix the crew — move it, or remove bookings that should not be there — and **Publish**, or ask them to change the planned start. The badge clears once the two dates match or no published crew is left; staged changes do not count until they are published.',
            'To work the list somewhere else, export it with **⬇ Excel** — the file says it is filtered, and each flagged row lists both dates.',
          ],
          watchOut: [
            'The badge only says the two dates differ — not which one is wrong — and it never changes either date itself.',
            // ⚠️ "On the grid". The card's crew-summary read excludes deleted
            // projects only, and ProjectWorkspace computes the flag with no
            // status check, so a finished job can keep its banner there — and
            // step 3 sends the reader to that card (Codex, #545). And NOT "not on
            // the grid": getSchedule returns any project with crew in the window,
            // so a finished or archived job with booked crew still renders; it
            // misses the options maps and so gets no dates and no badge
            // (useScheduleData, the note on mergedRows) (Codex, #545). And not
            // "old crew to clear" for all of it: paged back, those rows show past
            // weeks, which clearFutureEntries never touches (work_date >= today)
            // and which are history (Codex, #545).
            'A change someone else makes can take a few minutes to show — the grid refreshes about every **five minutes**. **Complete**, **Canceled** and archived jobs never get the badge on the grid, even when their crew still shows there — there is no start date left to agree. Crew in past weeks is history and stays; only crew still booked from today on is worth clearing. The job\'s **Schedule & Dates** card can still show the warning.',
          ],
          deepLink: '/manpower',
          // ⚠️ EDITORS ONLY, though it sits among the reading topics. Its steps
          // end in "move the crew and Publish", which a read-only viewer of the
          // board has no controls for — and the track's rule is that editing
          // instructions are hidden from them. Both editor permissions, because a
          // fab editor resolves the mismatch on fab jobs. Read-only viewers still
          // get what the badge means from the appendix's
          // mod-schedule-crew-mismatch, which is ungated (Codex, #545).
          access: { anyPermission: ['manpower.edit', 'manpower.edit.fab'] },
        },
        {
          id: 'mp-fab-two-lines',
          title: 'Fabrication jobs carry two crew lines',
          summary:
            'A fabrication job renders a **Fabrication** line for the shop and an **Installation** line for the field crew, so the two kinds of labor never blur. In the Fabrication scope the footer adds shop-versus-field totals.',
          watchOut: [
            'An older entry recorded before the two-line split displays on the Fabrication line; editing it moves the value onto the phase you typed, so nothing double-counts.',
          ],
          access: { hideFromFieldLeaderOnly: true },
        },
      ],
    },

    // ── 2. Editing ──────────────────────────────────────────────────────────
    {
      id: 'mp-editing',
      title: 'Editing: stage, then publish',
      // SHARED ACTIONS ONLY, and no blanket "everything stages" promise. The
      // row-level ✕ clear hits the live board directly, but that exception
      // lives in mp-row-tools (with its warning) because this intro is also
      // rendered to fab editors, whose section drops that topic entirely —
      // "called out below" pointed at a topic they don't have (Codex, #353,
      // rounds six and seven).
      intro:
        'Typing into cells, moving a row with the stepper, and shifting a run all stage as drafts that only you can publish.',
      topics: [
        {
          id: 'mp-edit-mode',
          title: 'Entering edit mode',
          summary:
            'All editing sits behind one button: **✎ Edit Schedule**. It only appears on the daily ranges, because you edit days, not weeks.',
          steps: [
            'Set **Range** to **2 wk** or **4 wk** — on the weekly ranges the button is not offered.',
            'Click **✎ Edit Schedule**. If you already have staged work waiting it reads **✎ Edit Schedule (N pending)**.',
            'Click a day cell and type — **Enter** or **Tab** commits, **Esc** cancels. Use the **−  +** stepper to set the row\'s rate across the days on screen — it changes only the visible, unpinned days, so a run longer than the window keeps its old rate beyond the screen.',
            'The **? Help** button opens **How the Crew Schedule works**, a reference for every control and cell marker.',
          ],
          watchOut: [
            'Switching to a weekly range closes edit mode. Your staged changes keep — you just cannot type until you come back to 2 or 4 weeks.',
          ],
          access: { anyPermission: ['manpower.edit', 'manpower.edit.fab'] },
        },
        {
          id: 'mp-drafts-publish',
          title: 'Drafts are yours until you publish',
          summary:
            'Staged changes are invisible to PMs — the live board and every project tab keep showing the published schedule until you press Publish. Publish and Reset touch only what **you** staged, never a colleague\'s drafts.',
          steps: [
            'The tray across the top counts your **unpublished changes** while you work.',
            '**Publish (N)** writes your drafts to the live schedule and posts a notice on each affected job\'s activity feed, so the PM hears about it.',
            '**Reset changes** throws your drafts away. **Close (keep draft)** leaves them staged for next time.',
          ],
          watchOut: [
            'A **👥 staged by** pill means a colleague has drafts of their own on this board. You cannot publish or reset theirs — but if you retype a cell they staged, that cell becomes yours.',
            'A job you have just crewed stays under **⚠ Needs Scheduling** until you publish — the grouping reads the published schedule, so rows do not jump while you are still staging.',
          ],
          access: { anyPermission: ['manpower.edit', 'manpower.edit.fab'] },
        },
        {
          id: 'mp-cell-markers',
          title: 'What the cell markers mean',
          // NOT the help modal's "dashed = staged, solid = live" rule of
          // thumb: the marker code gives a published PLAIN change no outline
          // at all (blue only while isDraft; solid is reserved for pins), so
          // that generalization promises a solid-blue confirmation that never
          // appears (Codex, #353, round eight). Enumerate the three real
          // markers instead.
          summary:
            'A marked cell is one of three things: dashed blue is a staged plain change, dashed orange is a staged pinned day, and solid orange is a live pinned day. A published plain change carries no marker at all — after Publish, no news is good news.',
          watchOut: [
            'A **pin** happens automatically when a typed day differs from the row\'s base rate — the steppers leave pinned days alone. Retype the row\'s base rate into the cell to un-pin it.',
          ],
          access: { anyPermission: ['manpower.edit', 'manpower.edit.fab'] },
        },
        {
          id: 'mp-shift',
          title: 'Shifting a whole job',
          summary:
            'When a job slips, **⇄ Shift** moves its scheduled run from today forward by a number of days — including days beyond the dates on screen. Past days are never moved off where they were worked.',
          steps: [
            'In edit mode, use **⇄ Shift** on the row, set the day count, and **Apply**.',
            'The shift stages like any other edit — publish it when you are sure.',
            'Afterwards, review Safety and Survey visits for conflicts — the confirmation reminds you.',
          ],
          watchOut: [
            'Shifting **backward** can land crew on days that already carry it — booked past days included. The page counts those collisions and asks before overwriting; the whole move stages, so **Reset changes** undoes it any time before you publish.',
            'A very long run can exceed the staging cap; the page will ask you to shift it in stages rather than in one move.',
          ],
          access: { anyPermission: ['manpower.edit', 'manpower.edit.fab'] },
        },
        {
          id: 'mp-setup-crew',
          title: 'Setting up a job\'s crew in one pass',
          summary:
            '**📅 Set up** books a whole job at once: a date range, the working days, and a crew per day — with separate Fabrication and Installation figures on a fab job.',
          steps: [
            'In edit mode, use **📅 Set up** on the row.',
            'Set **Start**, **End** and **Working days**, then **Crew per day** — or the per-phase figures on a fab job (leave a phase blank to skip it).',
            'Read the preview: it sets the schedule from today on, and warns how many existing day cells it will replace or clear — including any outside the dates on screen.',
            '**Stage crew** — the set-up stays a draft until you publish it.',
          ],
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-row-tools',
          title: 'Row housekeeping: clear, sub-projects, and tags',
          summary:
            'The row cluster also clears a job\'s future crew (**✕**), adds a sub-project under a maintenance job (**＋ Sub**), and tags a row (**🏷**) as a maintenance project or as self-perform labor loaned to a GC.',
          watchOut: [
            '**✕ Clear crew from today on** is **live**, not staged — after the confirm it removes the job\'s future published crew immediately. There is no publish step to catch it.',
            'A maintenance parent row shows a read-only subtotal of its sub-projects — crew is booked on the subs, never on the parent.',
          ],
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-fab-scope',
          title: 'Editing as a fabrication-scoped editor',
          // Phrased CONDITIONALLY ("when fabrication is the only part…") on
          // purpose: roles union, so a reader can hold FabManpowerEditor AND
          // the full-schedule role — the grid then treats them as a full
          // editor (`fabEditOnly` is `!fullEdit && fab`), and a flat "your
          // role edits fab only" would be false for them. AccessRule cannot
          // express "fab role and NOT manpower.edit", so the copy carries the
          // condition instead (Codex, #353, second round).
          summary:
            'When fabrication is the only part of the schedule your account can edit, you work on Fabrication-division jobs only — both the shop and installation lines. Every other row is read-only for you, and Publish folds in just your fabrication drafts.',
          watchOut: [
            'Cells on other divisions\' jobs simply do not respond, and their hover text says why. If a write slips through anyway, the server refuses it and names the blocked job numbers.',
            'The row tools (**📅 Set up**, **✕**, sub-projects, tags) are not rendered for you at all. The Field Leaders roster and the **Available MP / day** figure stay visible but read-only — maintaining them belongs to the full-schedule editors.',
          ],
          // By ROLE, not `anyPermission: ['manpower.edit.fab']` — allows() lets
          // Admin pass every permission rule, and the grid's own fabEditOnly is
          // `!fullEdit && fab`, so a permission rule showed "your role edits
          // Fabrication jobs only" to readers whose grid is unrestricted
          // (Codex, this PR). A role rule names the person the copy is about.
          access: { anyRole: ['FabManpowerEditor'] },
        },
      ],
    },

    // ── 3. Beyond the grid ──────────────────────────────────────────────────
    {
      id: 'mp-beyond',
      title: 'Beyond the grid',
      topics: [
        {
          id: 'mp-leaders',
          title: 'The Field Leaders roster',
          summary:
            'The **LEADERS** toggle switches the page to the field-leader roster: who is assigned where, when they finish, and where they go next — with badges for gaps and missing next assignments.',
          steps: [
            'Switch with **LEADERS** at the top of the Manpower page, and narrow with the **Division:** chips.',
            'Sort by **Finish Date** or **Next Available** to see who frees up first.',
            'Maintain the roster with **+ Add Field Leader**, **Edit**, and **Remove**.',
          ],
          watchOut: [
            '**Remove** deactivates the person but preserves their history — assignments and schedule entries are kept.',
          ],
          deepLink: '/manpower/leaders',
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-available-mp',
          title: 'Available MP / day, Temps active and Subcontractors',
          summary:
            'The capacity figure the whole board is judged against: **Grand Total** turns yellow and **Headroom** goes negative when the schedule books more people than this.',
          steps: [
            'Click the number next to **Available MP / day** to change it — it takes effect for every viewer immediately.',
            'Click the number next to **Temps active** to enter how many temps are working now. Keep it current: nothing updates it for you, and the date under it shows everyone how old it is.',
            'Click the number next to **Subcontractors** to enter how many subcontractor workers are on our local jobsites now. The Training and Crew Scheduling Manager owns this figure and keeps it current the same way.',
          ],
          watchOut: [
            'Temps active and Subcontractors **together can\'t be more than Available MP / day**, because both are counted inside it. A save that would take them over is refused with the reason. If you lower Available MP below their total, the lines under both boxes turn red until the figures are corrected.',
          ],
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-pm-view',
          title: 'What your publish feeds',
          summary:
            'Inside each job, the Manpower tab shows the PM your **published** schedule only: a Scheduled Crew card, and a planned-versus-actual comparison against payroll — measured in crew-days, one person on the job for one day.',
          watchOut: [
            'Both sides of planned-vs-actual stop at the payroll feed\'s cutoff, so a week still being worked shows a dash rather than a false shortfall.',
            'A job with no published crew shows no Scheduled Crew card at all — which to a PM reads as "nothing scheduled". Publishing is what makes your plan visible.',
          ],
          access: { hideFromFieldLeaderOnly: true },
        },
      ],
    },

    // ── 4. Requests ─────────────────────────────────────────────────────────
    // The Manpower Request module (#303, September 2026). Facts verified
    // against manpower-requests.service / RequestsView / TriageRequestModal:
    //   • The inbox is readable by anyone who reaches /manpower; Triage… is
    //     rendered only for `manpower.edit` holders (canEdit && !fabEditOnly).
    //   • Moves: received → reviewed | booked | declined; reviewed → booked |
    //     declined; booked/declined → reviewed (reopen). Decline and reopen
    //     REQUIRE a note. A second scheduler moving the same row gets a 409.
    //   • "Booked" never writes the grid (operations, 2026-08-31).
    //   • SYSTAC forecast and PCO requests are proposed manpower, bookable as
    //     anticipated crew (product owner, 2026-09-02). pco → co on the Intacct link;
    //     rejected change → its open requests auto-decline.
    {
      id: 'mp-requests',
      title: 'Crew requests: the inbox',
      intro:
        'PMs ask for crew from inside a job; estimators send SYSTAC forecast updates the same way. Every ask lands in one inbox, and what you decide there is recorded against the job.',
      topics: [
        {
          id: 'mp-req-inbox',
          title: 'Where requests arrive',
          summary:
            'The **REQUESTS** toggle at the top of the Manpower page opens the inbox. The count on the toggle is how many requests still need a decision. New requests also post to the **Manpower Requests** team in Teams.',
          steps: [
            'Open **REQUESTS**. **Needs action** is the default tab — everything received or under review. **Booked**, **Declined**, **Archived** and **All** are one click away, and the type filter narrows to PM requests, SYSTAC forecasts, PCO work or CO work.',
            'Each row shows the job, the crew asked for exactly as typed, the dates, who raised it and the PM, and the reason in full. A row for a change order shows the change number under the type.',
            'Use **Older** and **Newer** to page; the list is newest first.',
          ],
          watchOut: [
            'Crew size is **free text** — "2-3", "Whole crew" and "1 leader + 3" are all real asks. Read it; nothing parses it.',
            '**Archived** rows are the old Formstack Manpower Change Form history, imported in September 2026. Rows from before December 2025 carry only a reason — the form had no crew or date fields then — and show **reason only** in the Crew column.',
          ],
          deepLink: '/manpower/requests',
          access: { hideFromFieldLeaderOnly: true },
        },
        {
          id: 'mp-req-triage',
          title: 'Deciding a request',
          summary:
            '**Triage…** on a row opens the request with three moves: **Mark reviewed** while you talk it through with the PM, **Booked** once the crew is on the grid, or **Decline**. Your note goes back to the PM on the request.',
          steps: [
            'Open **Triage…** on the row. The summary at the top is the ask; decide against that, not from memory.',
            'Pick the move. **Decline** needs a note — the PM reads it without the conversation. **Reopen for review** on a booked or declined request needs one too, saying what changed.',
            '**Save decision**. The row moves tabs, the badge updates, and the job\'s Overview feed gets a line saying what you decided.',
          ],
          watchOut: [
            '**Booked records that you have already entered the crew on the grid by hand.** It does not enter it. The schedule is your act, made after the conversation with the PM — a request never writes the crew schedule on its own.',
            'If the modal says someone else changed the request a moment ago, another scheduler got there first. Close it and look at the row again rather than saving over their decision.',
            'A **SYSTAC forecast** or **PCO work** request is proposed manpower, not a firm ask. Book it as **anticipated** crew so the schedule shows what may be coming; the estimator or PM is telling you, not booking you.',
            'A PCO request becomes a **CO work** request by itself the moment the PM links the change to an Intacct CO — the row changes type, your decision stands. If the change is rejected instead, its open requests are declined automatically with a note saying why.',
          ],
          deepLink: '/manpower/requests',
          access: { anyPermission: ['manpower.edit'] },
        },
      ],
    },

    // ── 5. Travel ───────────────────────────────────────────────────────────
    // The travel roster (#396) and Travel Acknowledgments (#542). Facts
    // verified against TravelRosterView, GapAnalysisView, ManpowerPage,
    // TravelAcknowledgmentsCard, manpower.controller, travel-roster.service
    // and travel-acknowledgment.service (2026-09-23):
    //   • The roster is not its own tab: Gap Analysis ▸ "Show travel roster".
    //     /manpower/travel still resolves, to Gap Analysis. Reads are
    //     unscoped; Add/Edit/Remove render only for manpower.edit (or Admin).
    //   • Acknowledgments are manpower.edit ONLY, by the product owner's
    //     ruling (2026-09-22): issue, list and entitlement routes all check
    //     it, and the card renders nothing without it. A fab editor does not
    //     hold it, so these topics must not reach them.
    //   • The Paycor job title is matched on the roster row's Employee ID, so
    //     a row with no ID has no title at all.
    //   • Only the newest link for a person on a job can be signed; a signed
    //     row is never replaced. The traveler never sees the caveats.
    {
      id: 'mp-travel',
      title: 'Travel: the roster and acknowledgments',
      intro:
        'Who can be sent out of town and when they come free, and the form each traveler signs for the pay and policy on a job.',
      topics: [
        {
          id: 'mp-travel-roster',
          title: 'The travel roster',
          summary:
            'The travel roster lists the field staff who can be sent out of town, and when each one is free. It is not a tab of its own: it sits at the bottom of **Gap Analysis** on the Manpower page.',
          steps: [
            'Open **Gap Analysis** at the top of the Manpower page, then **▸ Show travel roster** at the bottom.',
            'Read the tiles. **Traveler**, **50/50** and **Local** are separate counts, so an occasional traveler is never folded into the **Traveler** figure. **On the roster** is everyone active, whatever their preference — not the number you can send.',
            'Narrow the table with the **Travel** and **Division** filters. **Show former staff** brings back people who have left.',
          ],
          watchOut: [
            '**Available from** is typed by hand, not calculated — the crew schedule records how many people are on a job, never which people. A blank means **free now**, and a date is only as current as the last time somebody updated it.',
            'The **Vehicle** column records who has a company truck, for scheduling. It does not change travel pay.',
          ],
          deepLink: '/manpower/gap',
          access: { hideFromFieldLeaderOnly: true },
        },
        {
          id: 'mp-travel-roster-edit',
          title: 'Keeping the roster current',
          summary:
            'You add, edit and remove people here. A row matters beyond this screen: a Travel Acknowledgment is issued against it, and the name on the row is the name on the form the traveler signs.',
          steps: [
            'Use **Add person**, or **Edit** on a row. Fill in the first and last name, **Employee ID**, **Division** and **Travel preference**, then **Save**.',
            'Set **Available from** when someone is committed until a known date. Leave it blank when they are free now.',
            '**Company vehicle** has three settings. **Use the job title** follows the Paycor title; the other two record what you know. A **check** marker on a row means the title-based answer is a guess worth confirming.',
            '**Remove** marks the person as former staff. They drop out of the counts and out of the Travel Acknowledgments picker, but the row is kept rather than deleted, so anything already issued to them stays on file. **Show former staff** brings them back into view.',
          ],
          watchOut: [
            'Enter the **Employee ID** whenever you have it. It is how the row finds the person\'s Paycor job title, which decides the management rate — a row without one shows **no id** and has no job title. A re-import can still match it by name, but only while no other row without an ID has the same name.',
          ],
          deepLink: '/manpower/gap',
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-travel-ack-issue',
          title: 'Issuing a Travel Acknowledgment',
          summary:
            'A Travel Acknowledgment is the form a traveler signs to confirm the travel pay and policy for one job. You issue a link from the job, then send it yourself — the Project Tracker sends nothing.',
          steps: [
            'Open the job, go to its **Manpower** tab, and find the **Travel Acknowledgments** card.',
            'Pick the person from **Choose someone…** and click **Issue link**.',
            'Click **Copy link** on the new row and send it by text or email.',
            'The traveler opens it on their phone with no account and no sign-in, reads it in English or Spanish, types their full name, checks the box and signs. Their row then shows who signed and when.',
          ],
          watchOut: [
            'The whole form is fixed **when you issue the link** — nothing on it updates afterward. Issue a new link if something it shows or is calculated from changes: the job number or title, the site address, the mobilization date, the travel band, prevailing wage, the person\'s name, their Paycor job title, or their Regional Office. Other job details, such as the PM, are not on the form and need no new link. A form already signed keeps saying what it said; an unsigned one is replaced and stops working.',
            'The **Mobilization Date** is the job\'s next mobilization on or after the day you issue the link, or its most recent one if none is still to come. A sub-project with no mobilizations of its own uses its parent job\'s, and a job with none at all shows its planned start instead. A sub-project always shows its parent job\'s site address.',
          ],
          deepLinkNote: 'Project workspace ▸ Manpower ▸ Travel Acknowledgments card',
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-travel-ack-list',
          title: 'Reading the acknowledgments list',
          summary:
            'Each row on the card is one link. **Not signed** is waiting on the traveler; a name and date is the signature; **Replaced by a newer link** means you issued another since, and this one no longer works.',
          watchOut: [
            'Only the **newest** link for a person on a job can be signed. Issuing again to someone whose link is unsigned replaces it — the card asks first — and the old link then tells the traveler it is not valid. A signed form is never replaced.',
            'A warning under a link is about that person\'s pay, and most are tidying-up: a job title or vehicle question that does not change what this job pays, which is why the link was issued. The exception is **exempt status**, shown for management titles — it is recorded nowhere, and an exempt traveler may not be eligible at all, so settle that one before you send the link. The traveler never sees these warnings.',
          ],
          deepLinkNote: 'Project workspace ▸ Manpower ▸ Travel Acknowledgments card',
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-travel-ack-file',
          title: 'Filing a signed acknowledgment in SharePoint',
          summary:
            'Once someone signs, file their form in the job\'s SharePoint folder so the job keeps its own copy. One click makes a PDF of exactly what they signed and saves it there.',
          steps: [
            'On the job\'s **Manpower** tab, find the signed row on the **Travel Acknowledgments** card.',
            'Click **File to SharePoint**. The first time anyone files on a job, this creates a **Travel Acknowledgments** folder inside the job\'s SharePoint folder.',
            'The row then shows **Filed · Open in SharePoint**, which opens the PDF. Hover over it to see who filed it and when.',
          ],
          watchOut: [
            'The PDF is the form **as it was signed**, in the language the traveler signed in — the same text, rates and name they saw, with the date and time they signed. It does not pick up anything changed since.',
            'It saves with **your own** SharePoint access, so you need access to the job\'s folder. If the job has no SharePoint folder, the row says so — link or create one on the job\'s **Documents** tab first.',
            'Each acknowledgment is filed once. If someone else filed it first, the card shows their link instead.',
            'If the message says the PDF **is in SharePoint but was not recorded**, click **File to SharePoint** again. It finds the file already there and records it, rather than saving a second copy.',
          ],
          deepLinkNote: 'Project workspace ▸ Manpower ▸ Travel Acknowledgments card',
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-travel-ack-refused',
          title: '"Issue link" is refused',
          summary:
            'The card refuses rather than send a form that cannot say what someone is owed. The message names what is missing; this is who fixes each one.',
          table: {
            headers: ['The message is about', 'Who fixes it'],
            rows: [
              ['No travel band on the job', 'The estimator, on the job\'s **Job Info** card'],
              ['The job\'s site address is not verified', 'Whoever edits the job — pick the address from the search on its **Job Info** card. An older job can show an address that was never picked; it still needs picking'],
              ['No mobilization and no planned start', 'Whoever edits the job — add a mobilization on its **Mobilization** tab, or a planned start on its **Schedule** tab'],
              ['No name on the roster row', 'You — add it on the travel roster, then issue again'],
              ['No Paycor job title found', 'Usually the roster row\'s **Employee ID** is missing or does not match Paycor — check it against Paycor and correct it. Only if it matches is the title itself missing in Paycor'],
              ['More than one Paycor job title for the employee ID', 'The duplicate has to be fixed in Paycor'],
              ['No Assigned Regional Office in Paycor', 'Check the roster row\'s **Employee ID** first, as for a job title. If it matches, the office has to be set in Paycor — it reaches the Project Tracker with the next roster sync, so the link can be issued after that'],
              ['More than one Assigned Regional Office for the employee ID', 'The duplicate has to be fixed in Paycor, then wait for the next roster sync'],
              ['A job title nobody has ruled on', 'HR decides whether it carries the management rate, and the ruling then has to be added to the Project Tracker before the link can be issued — ask the admin'],
            ],
          },
          watchOut: [
            'A job title only blocks the link where it changes the pay — the management rate applies on jobs of 121 miles or more that are not prevailing wage. Anywhere else the link is issued and the question shows as a warning under it instead.',
          ],
          access: { anyPermission: ['manpower.edit'] },
        },
        {
          id: 'mp-travel-payroll-report',
          title: 'The travel payroll report',
          summary:
            'Every Travel Acknowledgment signed in a date range, with each rate exactly as the traveler signed it, for payroll. It lists **rates, not amounts** — how many days each rate applies to is payroll\'s call.',
          steps: [
            'Open **Gap Analysis** at the top of the Manpower page, then **▸ Show travel payroll report** at the bottom.',
            'Pick the dates, or **Last pay week** for the Sunday–Saturday just ended, and press **Show**.',
            'Use **Download Excel** for the workbook payroll receives: one row per rate, with the person and job on every row.',
          ],
          watchOut: [
            'Once the weekly email is switched on, last pay week\'s report is emailed to payroll on Monday morning. A failed send shows in red on the panel and is tried again each morning through Saturday; after that the week is not tried again, so re-send it yourself. A send marked **not confirmed** is never retried, because it may already have gone: check the sending mailbox\'s Sent Items, and re-send only if it is not there.',
            '**Email these dates to payroll now** re-sends whatever dates are shown, and is logged under your name. Use it for a late signature or a failed week, not in place of the weekly email.',
            '**Replaced by a later acknowledgment** means the person signed again on the same job, usually after the band changed. Payroll should use the later one.',
          ],
          deepLink: '/manpower/gap',
          access: { anyPermission: ['manpower.edit', 'travel.payroll_report'] },
        },
      ],
    },

    // ── 6. Troubleshooting ──────────────────────────────────────────────────
    {
      id: 'mp-troubleshooting',
      title: 'When something looks wrong',
      topics: [
        {
          id: 'mp-no-edit-button',
          title: '"I can\'t find the Edit button"',
          summary: 'Two causes, both quick: the range, or the role.',
          steps: [
            'Check **Range** — **✎ Edit Schedule** only appears on **2 wk** and **4 wk**, because edits are per day.',
            'If the banner says you are in read-only mode, your account does not carry schedule editing — ask the admin.',
          ],
          access: { hideFromFieldLeaderOnly: true },
        },
        {
          id: 'mp-job-missing',
          title: '"A job is not on the grid"',
          summary: 'Almost always scope or a filter, not missing data.',
          steps: [
            'Check the scope — **Self-Perform** leaves fabrication jobs off entirely.',
            'Check the **Show:** chips, the search box, and the **PM** filter — a filter set an hour ago still applies.',
            // Unlike every other chip, this one SHOWS ONLY rather than hides —
            // so a scheduler who left it on after working the list sees half the
            // board and nothing saying why, unless they look at the chip (#385).
            'Check whether **⚠ Start ≠ Crew** is switched on — it shows **only** the jobs whose dates disagree, so every other job drops out until you click it off.',
            'A job **starting** in the visible window with no crew booked in those weeks sits under **⚠ Needs Scheduling** at the top. A job already underway stays under its division even when the window shows no crew for it.',
          ],
          access: { hideFromFieldLeaderOnly: true },
        },
      ],
    },
  ],
};
